import { WebsocketClient, WebsocketMessage } from "./websocket-client";
import { store } from "../store";
import {
	updateSingleReservation,
	addReservation,
	updateSingleOccupiedDay,
	removeReservation,
	panic,
	addInquiry,
	updateSingleInquiry,
	removeInquiry
} from "../actions/store/app-actions";
import {
	transformOccupiedDayResponse,
	OccupiedDay
} from "../api/occupied-days";
import { transformReservationResponse, Reservation } from "../api/reservations";
import { setSnackbarMessage } from "../actions/store/snackbar-actions";
import { updateDateRelatedData, updateInquiryList } from "../api/setup";
import { Inquiry, transformInquiryResponse } from "../api/inquiries";
import { format } from "../util/date-utility";
import { Payment } from '../api/payments';

export class RealtimeSync {
	private client: WebsocketClient;
	private lastCount: number = null;
	private listening = false;
	private static uuid: string = null;
	private static instance: RealtimeSync = null;

	constructor() {
		if (RealtimeSync.instance === null) {
			this.client = new WebsocketClient();
			RealtimeSync.instance = this;
		} else {
			this.lastCount = null;
			console.warn("RealtimeSync class can only be construced once!");
		}

		return RealtimeSync.instance;
	}

	public start() {
		this.listenForMessages();
		this.client.connect();
	}

	public stop() {
		this.lastCount = null;
		this.client.disconnect();
	}

	public static getUUID() {
		return RealtimeSync.uuid;
	}

	private handleReservationChange(data: WebsocketMessage<Reservation>) {
		switch (data.action) {
			case "add":
				store.dispatch(
					addReservation(transformReservationResponse(data.payload))
				);
				break;

			case "update":
				store.dispatch(
					updateSingleReservation(
						data.payload.id,
						transformReservationResponse(data.payload)
					)
				);
				break;

			case "delete":
				store.dispatch(removeReservation(data.payload.id));
		}
	}

	private handleInquiryChange(data: WebsocketMessage<Inquiry>) {
		switch (data.action) {
			case "add":
				store.dispatch(
					addInquiry(transformInquiryResponse(data.payload))
				);
				store.dispatch(
					setSnackbarMessage(
						`<strong>Neue Anfrage von ${
							data.payload.primaryName
						}!</strong> <br> ${
							data.payload.peopleCount
						} Personen am ${format(
							data.payload.date,
							"DD.MM."
						)}${data.payload.time &&
							" um " + format(data.payload.time, "HH:mm")}`,
						"success",
						{
							type: "inquiry",
							data: data.payload
						}
					)
				);
				break;

			case "update":
				store.dispatch(
					updateSingleInquiry(
						data.payload.id,
						transformInquiryResponse(data.payload)
					)
				);
				break;

			case "delete":
				store.dispatch(removeInquiry(data.payload.id));
				break;
		}
	}

	private handleOccupiedDayChange(data: WebsocketMessage<OccupiedDay>) {
		switch (data.action) {
			case "add":
			case "update":
				store.dispatch(
					updateSingleOccupiedDay(
						data.payload.id,
						transformOccupiedDayResponse(data.payload)
					)
				);
				break;
		}
	}

	private handlePaymentChange(data: WebsocketMessage<Payment>) {
		switch (data.action) {
			case "update":
				if (data.payload.state === "completed") {
					store.dispatch(
						setSnackbarMessage(
							"Neuer Zahlungseingang!",
							"success"
						)
					);
				}
				break;
		}
	}

	private checkModel(data: WebsocketMessage<any>) {
		if (this.lastCount === data.count - 1) {
			this.lastCount = data.count;

			if (!RealtimeSync.uuid) {
				console.warn(
					"No uuid exists for current realtime sync session!"
				);
			}

			if (data.origin !== RealtimeSync.uuid) {
				switch (data.model) {
					case "reservation":
						this.handleReservationChange(data);
						return;

					case "inquiry":
						this.handleInquiryChange(data);
						return;

					case "occupied-day":
						this.handleOccupiedDayChange(data);
						return;

					case "payment":
						this.handlePaymentChange(data);
						return;
				}
			}
		} else {
			this.handleDesync();
		}
	}

	private async handleDesync() {
		store.dispatch(
			setSnackbarMessage(
				"Desynchronisation! Verbindung wird wiederhergestellt...",
				"warning"
			)
		);

		try {
			await updateDateRelatedData();
			await updateInquiryList();
		} catch (e) {
			store.dispatch(panic());
		}
	}

	private initConnection(data: WebsocketMessage<any>) {
		if (this.lastCount !== null && this.lastCount !== data.count) {
			this.handleDesync();
		}

		RealtimeSync.uuid = data.payload.uuid;
		this.lastCount = data.count;
	}

	private checkMessage(data: WebsocketMessage<any>) {
		switch (data.type) {
			case "init":
				this.initConnection(data);
				break;

			case "change":
				this.checkModel(data);
				break;
		}
	}

	private listenForMessages() {
		if (!this.listening) {
			this.listening = true;

			this.client.on(data => {
				this.checkMessage(data);
			});
		}
	}
}
